import {
  EMAIL_BODY_CONTENT_STYLES,
  EMAIL_FOOTER_PARTIAL,
  EMAIL_HEADER_PARTIAL,
} from './email-template-design.constants';

const SYSTEM_MACRO_KEYS = new Set([
  'logo',
  'support_email',
  'website_name',
  'no_reply_email',
  'year',
  'public_url',
  'website_tagline',
]);

const PREVIEW_MACRO_SAMPLES: Record<string, string> = {
  name: 'Sample User',
  email: 'user@example.com',
  otp: '123456',
  resetPassword: '#',
  portal_link: '#',
  tenant_name: 'Sample Tenant',
  purchase_order_no: 'PO-778899',
  purchase_order_date: '2026-05-02',
  item_count: '12',
  po_value: 'USD 45,000.00',
  recipient_name: 'Recipient User',
  company_name: 'Acme Corporation Ltd.',
};

const PREVIEW_LOGO =
  'https://agenticpd-assets.s3.ap-south-1.amazonaws.com/public/agenticpd/agenticpd-white-logo.png';

const PARTIALS: Record<string, string> = {
  'email-header': EMAIL_HEADER_PARTIAL,
  'email-footer': EMAIL_FOOTER_PARTIAL,
};

export function parseMacroKeys(macro: string | null | undefined): string[] {
  if (!macro?.trim()) {
    return [];
  }
  const safeMacro = macro.slice(0, 2000);
  const keys: string[] = [];
  for (const segment of safeMacro.split(',')) {
    let token = segment.trim();
    if (!token) continue;

    if (token.startsWith('{{')) {
      token = token.slice(2).trimStart();
    }
    if (token.endsWith('}}')) {
      token = token.slice(0, -2).trimEnd();
    }

    if (
      token &&
      !token.startsWith('>') &&
      !SYSTEM_MACRO_KEYS.has(token) &&
      !keys.includes(token)
    ) {
      keys.push(token);
    }
  }
  return keys;
}

export function buildPreviewContext(macroKeys: string[]): Record<string, string> {
  const ctx: Record<string, string> = {
    logo: PREVIEW_LOGO,
    website_name: 'Agentic PD',
    year: String(new Date().getFullYear()),
    support_email: 'support@agenticpd.com',
    no_reply_email: 'noreply@example.com',
    public_url: 'https://example.com',
  };

  for (const key of macroKeys) {
    if (SYSTEM_MACRO_KEYS.has(key)) {
      continue;
    }
    if (ctx[key] !== undefined) {
      continue;
    }
    const sample = PREVIEW_MACRO_SAMPLES[key];
    ctx[key] = sample ?? `[${key}]`;
  }

  return ctx;
}

function renderPartials(html: string): string {
  return html.replace(
    /\{\{\s*(?:>|&gt;)\s*([a-zA-Z0-9_-]+)\s*\}\}/g,
    (match, name) => PARTIALS[name] ?? match,
  );
}

function renderMacros(html: string, context: Record<string, string>): string {
  return html.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => {
    return context[key] ?? match;
  });
}

function injectPreviewBaseStyles(html: string): string {
  const styleBlock = `<style type="text/css">${EMAIL_BODY_CONTENT_STYLES}</style>`;
  if (html.includes('</head>')) {
    return html.replace('</head>', `${styleBlock}</head>`);
  }
  if (/<html[\s>]/i.test(html)) {
    return html.replace(
      /<html([^>]*)>/i,
      `<html$1><head><meta charset="UTF-8">${styleBlock}</head>`,
    );
  }
  return `<!DOCTYPE html><html><head><meta charset="UTF-8">${styleBlock}</head><body>${html}</body></html>`;
}

export function compileEmailPreviewHtml(
  html: string,
  macroKeys: string[],
): string {
  const source = (html ?? '').trim();
  if (!source) {
    return '<!DOCTYPE html><html><body style="margin:0;padding:16px;font-family:Arial,sans-serif;color:#94a3b8;">No content to preview.</body></html>';
  }

  const withPartials = renderPartials(source);
  const compiled = renderMacros(withPartials, buildPreviewContext(macroKeys));
  return injectPreviewBaseStyles(compiled);
}

export function compileEmailSubjectPreview(
  subject: string,
  macroKeys: string[],
): string {
  const source = (subject ?? '').trim();
  if (!source) {
    return '(No subject)';
  }
  return renderMacros(source, buildPreviewContext(macroKeys));
}

export function renderEmailPreviewInIframe(
  frame: HTMLIFrameElement | null | undefined,
  html: string,
  macroKeys: string[],
): void {
  if (!frame) {
    return;
  }
  frame.srcdoc = compileEmailPreviewHtml(html, macroKeys);
}
